import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import MetaPixel from "./components/MetaPixel";
import GoogleAds from "./components/GoogleAds";
import { CallbackModalProvider } from "./contexts/CallbackModalContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import CookieBanner from "./components/CookieBanner";

// Eager load critical pages to avoid preview lazy-import fetch failures
import Home from "./pages/Home";
import Webshops from "./pages/diensten/Webshops";

// Lazy load other pages for smaller initial bundle
const Diensten = lazy(() => import("./pages/Diensten"));
const OverOns = lazy(() => import("./pages/OverOns"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Afspraak = lazy(() => import("./pages/Afspraak"));
const Kennismaken = lazy(() => import("./pages/Kennismaken"));
const Offerte = lazy(() => import("./pages/Offerte"));
const NotFound = lazy(() => import("./pages/NotFound"));

const WebsiteBusiness = lazy(() => import("./pages/diensten/WebsiteBusiness"));
const WebsitePro = lazy(() => import("./pages/diensten/WebsitePro"));
const WebsiteEnterprise = lazy(() => import("./pages/diensten/WebsiteEnterprise"));
const SEO = lazy(() => import("./pages/diensten/SEO"));
const EmailMarketing = lazy(() => import("./pages/diensten/EmailMarketing"));
const BolCom = lazy(() => import("./pages/diensten/BolCom"));
const Trainingen = lazy(() => import("./pages/diensten/Trainingen"));
const AI = lazy(() => import("./pages/diensten/AI"));
const Shopify = lazy(() => import("./pages/diensten/Shopify"));
const TikTokShop = lazy(() => import("./pages/diensten/TikTokShop"));
const PrivacyBeleid = lazy(() => import("./pages/PrivacyBeleid"));
const Servicevoorwaarden = lazy(() => import("./pages/Servicevoorwaarden"));
const WebshopGeenOmzet = lazy(() => import("./pages/blogs/WebshopGeenOmzet"));
const ShopifyVsWoocommerce = lazy(() => import("./pages/blogs/ShopifyVsWoocommerce"));
const SeoVoorBeginners = lazy(() => import("./pages/blogs/SeoVoorBeginners"));
const EmailMarketingNieuwsbrief = lazy(() => import("./pages/blogs/EmailMarketingNieuwsbrief"));
const AiWebsiteSlimmer = lazy(() => import("./pages/blogs/AiWebsiteSlimmer"));
const BrandingWebshop = lazy(() => import("./pages/blogs/BrandingWebshop"));
const TikTokShopNederland = lazy(() => import("./pages/blogs/TikTokShopNederland"));
const Blogs = lazy(() => import("./pages/Blogs"));
const AdminGate = lazy(() => import("./components/AdminGate"));
const Facturatie = lazy(() => import("./pages/Facturatie"));
const FacturatieBetaald = lazy(() => import("./pages/FacturatieBetaald"));
const CaseDetail = lazy(() => import("./pages/CaseDetail"));
const Gelukt = lazy(() => import("./pages/Gelukt"));
const Audit = lazy(() => import("./pages/Audit"));
const Vacatures = lazy(() => import("./pages/Vacatures"));
const SeoDashboard = lazy(() => import("./pages/SeoDashboard"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Laden...</div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <CookieConsentProvider>
          <CallbackModalProvider>
          <MetaPixel />
          <CookieBanner />
          <GoogleAds />
          <ScrollToTop />
          <Routes>
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/index" element={<Navigate to="/" replace />} />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="/onze-diensten" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Diensten />
                  </Suspense>
                </Layout>
              } />
              <Route path="/shopify-webshop" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Webshops />
                  </Suspense>
                </Layout>
              } />
              {/* Redirect van oude URL naar nieuwe URL */}
              <Route path="/webshopdesign" element={<Navigate to="/shopify-webshop" replace />} />
              <Route path="/websitedesign" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <WebsiteBusiness />
                  </Suspense>
                </Layout>
              } />
              <Route path="/websitedesign/pro" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <WebsitePro />
                  </Suspense>
                </Layout>
              } />
              <Route path="/websitedesign/enterprise" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <WebsiteEnterprise />
                  </Suspense>
                </Layout>
              } />
              <Route path="/diensten/seo" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SEO />
                  </Suspense>
                </Layout>
              } />
              <Route path="/email-marketing" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <EmailMarketing />
                  </Suspense>
                </Layout>
              } />
              <Route path="/diensten/bol-com" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <BolCom />
                  </Suspense>
                </Layout>
              } />
              <Route path="/diensten/trainingen" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Trainingen />
                  </Suspense>
                </Layout>
              } />
              <Route path="/ai-oplossingen" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <AI />
                  </Suspense>
                </Layout>
              } />
              <Route path="/diensten/shopify" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Shopify />
                  </Suspense>
                </Layout>
              } />
              <Route path="/tiktok-shop" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <TikTokShop />
                  </Suspense>
                </Layout>
              } />
              <Route path="/over-ons" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <OverOns />
                  </Suspense>
                </Layout>
              } />
              <Route path="/portfolio" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Portfolio />
                  </Suspense>
                </Layout>
              } />
              <Route path="/contact" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Kennismaken />
                  </Suspense>
                </Layout>
              } />
              <Route path="/kennismaken" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Kennismaken />
                  </Suspense>
                </Layout>
              } />
              <Route path="/offerte" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Offerte />
                  </Suspense>
                </Layout>
              } />
              <Route path="/privacybeleid" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <PrivacyBeleid />
                  </Suspense>
                </Layout>
              } />
              <Route path="/servicevoorwaarden" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Servicevoorwaarden />
                  </Suspense>
                </Layout>
              } />
              <Route path="/blogs" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Blogs />
                  </Suspense>
                </Layout>
              } />
              <Route path="/blogs/10-redenen-webshop-geen-omzet" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <WebshopGeenOmzet />
                  </Suspense>
                </Layout>
              } />
              <Route path="/blogs/shopify-vs-woocommerce" element={
                <Layout><Suspense fallback={<PageLoader />}><ShopifyVsWoocommerce /></Suspense></Layout>
              } />
              <Route path="/blogs/seo-voor-beginners" element={
                <Layout><Suspense fallback={<PageLoader />}><SeoVoorBeginners /></Suspense></Layout>
              } />
              <Route path="/blogs/email-marketing-nieuwsbrief" element={
                <Layout><Suspense fallback={<PageLoader />}><EmailMarketingNieuwsbrief /></Suspense></Layout>
              } />
              <Route path="/blogs/ai-website-slimmer" element={
                <Layout><Suspense fallback={<PageLoader />}><AiWebsiteSlimmer /></Suspense></Layout>
              } />
              <Route path="/blogs/branding-webshop" element={
                <Layout><Suspense fallback={<PageLoader />}><BrandingWebshop /></Suspense></Layout>
              } />
              <Route path="/blogs/tiktok-shop-nederland" element={
                <Layout><Suspense fallback={<PageLoader />}><TikTokShopNederland /></Suspense></Layout>
              } />
              <Route path="/facturatie" element={
                <Suspense fallback={<PageLoader />}><AdminGate><Facturatie /></AdminGate></Suspense>
              } />
              <Route path="/facturatie/betaald" element={
                <Suspense fallback={<PageLoader />}><FacturatieBetaald /></Suspense>
              } />
              <Route path="/afspraak" element={
                <Suspense fallback={<PageLoader />}><Afspraak /></Suspense>
              } />
              <Route path="/cases/:slug" element={
                <Layout><Suspense fallback={<PageLoader />}><CaseDetail /></Suspense></Layout>
              } />
              <Route path="/gelukt" element={
                <Suspense fallback={<PageLoader />}><Gelukt /></Suspense>
              } />
              <Route path="/audit" element={
                <Suspense fallback={<PageLoader />}><Audit /></Suspense>
              } />
              <Route path="/vacatures" element={
                <Layout><Suspense fallback={<PageLoader />}><Vacatures /></Suspense></Layout>
              } />
              <Route path="/seo-onderzoek" element={
                <Suspense fallback={<PageLoader />}><AdminGate><SeoDashboard /></AdminGate></Suspense>
              } />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <NotFound />
                  </Suspense>
                </Layout>
              } />
          </Routes>
          </CallbackModalProvider>
          </CookieConsentProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
